import React,{useState} from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Paper, TextField,} from "@mui/material";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { tokens } from "../theme";
import { useRovingRows } from "./useRovingRows";
import TableSkeleton from "./TableSkeleton";
const StyledLink = styled(Link)({
  color:"inherit",
  textDecoration:"none"
})

const miscStyle = {
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "150px",
}

const BackendPaginationTable = ({
  rows,
  headings,
  renderCell,
  customWidth,
  noRecordMessage = "",
  loading,
  link,
  path,
  id,
  tableStyle,
  paginationHide,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  // Total row count, when the caller knows it. The book asks pagination to
  // show the total — "page 3 of 12" answers a question people actually have —
  // and without it this control can only say which page you are on.
  count,
}) => {
  const { bodyRef, rowProps } = useRovingRows();

  const totalPages = count ? Math.max(1, Math.ceil(count / rowsPerPage)) : 0;

  // The book's prev/next: a bordered control, not a bare glyph, so it reads as
  // something you can press and shows plainly when it cannot be pressed.
  const pageBtnSx = {
    display: "flex", alignItems: "center", justifyContent: "center",
    border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px",
    padding: "6px 12px", background: "transparent", color: tokens.primary,
    cursor: "pointer", font: "inherit",
    "&:disabled": { color: tokens.textInactive, cursor: "default" },
    "&:focus-visible": { outline: `2px solid ${tokens.primary}`, outlineOffset: "2px" },
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = (parseInt(event.target.value, 10));
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handlePreviousPage = () => {
    if(page>0)
     {
      const newPage = page-1;
      setPage(newPage);
     }
  };

  const handleNextPage = () => {
    const newPage = page+1;
    setPage(newPage);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", boxShadow: "none", elevation: 0 }}>
        <TableContainer sx={tableStyle?{...tableStyle}:{}}>
          <Table aria-busy={Boolean(loading)} sx={{  minWidth: customWidth || 550 }}>
            <TableHead>
              <TableRow>
                {headings?.map((heading, index) => (
                  <TableCell key={index} sx={heading.style}>
                    {heading.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody ref={bodyRef}>
            {loading ? (
              <TableSkeleton columns={headings?.length || 4} />
            ) : rows?.length > 0
                ?  rows?.map((row, rowIndex) => (
                    <TableRow 
                      hover 
                      key={rowIndex}
                      {...rowProps(rowIndex)}
                      component={link ? StyledLink : 'tr'}
                      to={link ? `${path}/${row._id || row[id]}` : null} 
                    >
                      {renderCell(row)}
                    </TableRow>
                  ))
                : <TableRow>
                    <TableCell colSpan={headings?.length}>
                       <Box sx={miscStyle}>
                          {noRecordMessage}
                        </Box>
                    </TableCell>
                  </TableRow>
                }
            </TableBody>
          </Table>
        </TableContainer>
        {!paginationHide ? (
         <Box sx={{display:"flex", justifyContent:"end", alignItems:"center", gap: "1rem", fontSize:"0.8125rem", lineHeight: 1.5, marginTop: "1rem"}}>
                <Box sx={{display:"flex", alignItems:"center", gap: "0.5rem"}}>
                      <Box component="label" htmlFor="pageSizeSelect">Rows per page:</Box>
                      <select id="pageSizeSelect" value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                      </select>
                </Box>
                <Box sx={{display:"flex", alignItems:"center", gap: "8px"}}>
                      <Box aria-live="polite">
                        {totalPages ? `Page ${page + 1} of ${totalPages}` : `Page ${page + 1}`}
                      </Box>
                      <Box component="button" type="button" aria-label="Previous page"
                         disabled={page === 0}
                         onClick={()=> handlePreviousPage()}
                         sx={pageBtnSx}>
                         <ChevronLeftIcon sx={{ fontSize: "1rem" }} />
                      </Box>
                      <Box component="button" type="button" aria-label="Next page"
                         disabled={!!totalPages && page + 1 >= totalPages}
                         onClick={()=> handleNextPage()}
                         sx={pageBtnSx}>
                         <ChevronRightIcon sx={{ fontSize: "1rem" }} />
                      </Box>
                </Box>
            </Box>     
        ): null}
      </Paper>
    </Box>
  );
};

export default BackendPaginationTable;
